import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { DataFieldWithDataType } from '../../../../common-types';
import { useMutateUserRolePreferences } from '../../../common/v2/api/userrolepreferences/hooks';
import { UserRolePreferencesSettings } from '../../../common/v2/api/userrolepreferences/types';
import { buildNextUserRolePreferencesSettings } from '../../../common/v2/api/userrolepreferences/utils';
import { Filter } from '../../../common/v2/common/components/filter/filterbar/types';
import {
  useExecutorRoleContext,
  useUserRolePreferencesServiceContext,
  useUserRolePreferencesSettingsContext,
} from '../../../common/v2/context/contexts';
import { DEFAULT_DRILLDOWN_FIELDS } from './drilldownColumns';

/**
 * The Data Explorer columns saved for the current executor role, and a setter that persists
 * a new ordered list. Falls back to {@link DEFAULT_DRILLDOWN_FIELDS} until the role saves
 * its own selection.
 *
 * The returned fields are raw preferences: they are not filtered by permissions or by
 * whether the field still holds data. Pass them through `resolveDrillDownColumns` before
 * rendering or querying.
 */
export const useDataExplorerColumns = () => {
  const prefs = useUserRolePreferencesSettingsContext();
  const executorRole = useExecutorRoleContext();
  const { mutate } = useMutateUserRolePreferences<Filter>();
  const service = useUserRolePreferencesServiceContext<Filter>();
  const queryClient = useQueryClient();

  const saved = prefs?.settings?.dataExplorerColumns?.find(
    (c) => c.executorRoleId === executorRole.id
  )?.fields;

  const fields = saved ?? DEFAULT_DRILLDOWN_FIELDS;

  const saveFields = useCallback(
    (nextFields: DataFieldWithDataType[]) => {
      const otherRoles = (prefs?.settings?.dataExplorerColumns ?? []).filter(
        (c) => c.executorRoleId !== executorRole.id
      );
      const nextSettings = buildNextUserRolePreferencesSettings(
        prefs?.settings,
        {
          dataExplorerColumns: [
            ...otherRoles,
            { executorRoleId: executorRole.id, fields: nextFields },
          ],
        }
      );

      if (prefs) {
        queryClient.setQueryData<UserRolePreferencesSettings<Filter> | null>(
          ['userRolePreferences', ...service.getDeps()],
          { ...prefs, settings: nextSettings }
        );
      }
      mutate(nextSettings);
    },
    [prefs, executorRole.id, service, queryClient, mutate]
  );

  return { fields, saveFields };
};
